/**
 * Quick View Modal - mdlondon Design System
 * Handles product quick view modal display and functionality
 */

class QuickViewModal {
  constructor() {
    this.modal = null;
    this.init();
  }

  init() {
    this.createModal();
    this.setupEventListeners();
  }

  createModal() {
    const modal = document.createElement('div');
    modal.className = 'quick-view-modal';
    modal.innerHTML = '<div class="quick-view-modal__content">' +
      '<button class="quick-view-modal__close" aria-label="Close quick view">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor">' +
          '<line x1="18" y1="6" x2="6" y2="18"></line>' +
          '<line x1="6" y1="6" x2="18" y2="18"></line>' +
        '</svg>' +
      '</button>' +
      '<div class="quick-view-product quick-view-product--loading"></div>' +
    '</div>';

    document.body.appendChild(modal);
    this.modal = modal;
  }

  setupEventListeners() {
    // Quick view button clicks
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.quick-view-button, .product__quick-view-button');
      if (button) {
        e.preventDefault();
        const productHandle = button.dataset.productHandle;
        if (productHandle) {
          this.openQuickView(productHandle);
        }
      }
    });

    // Close button
    this.modal.addEventListener('click', (e) => {
      if (e.target.matches('.quick-view-modal__close') || e.target.closest('.quick-view-modal__close')) {
        this.close();
      }
    });

    // Close on overlay click
    this.modal.addEventListener('click', (e) => {
      if (e.target.classList.contains('quick-view-modal')) {
        this.close();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('quick-view-modal--active')) {
        this.close();
      }
    });

    // Variant selection
    this.modal.addEventListener('click', (e) => {
      const option = e.target.closest('.quick-view-product__variant-option');
      if (option && !option.classList.contains('quick-view-product__variant-option--unavailable')) {
        this.selectVariantOption(option);
      }
    });

    // Quantity buttons
    this.modal.addEventListener('click', (e) => {
      if (e.target.matches('.quick-view-product__quantity-button')) {
        const input = e.target.parentElement.querySelector('input');
        const currentValue = parseInt(input.value);
        const buttonText = e.target.textContent;

        if (buttonText === '-' && currentValue > 1) {
          input.value = currentValue - 1;
        } else if (buttonText === '+') {
          input.value = currentValue + 1;
        }
      }
    });

    // Add to cart
    this.modal.addEventListener('click', (e) => {
      if (e.target.matches('.quick-view-product__add-to-cart')) {
        e.preventDefault();
        this.addToCart();
      }
    });

    // Thumbnail clicks
    this.modal.addEventListener('click', (e) => {
      const thumbnail = e.target.closest('.quick-view-product__thumbnail');
      if (thumbnail) {
        this.switchImage(thumbnail);
      }
    });
  }

  async openQuickView(productHandle) {
    this.open();
    this.setLoading(true);

    try {
      const response = await fetch('/products/' + productHandle + '.js');
      const product = await response.json();
      this.renderProduct(product);
    } catch (error) {
      console.error('Error loading product:', error);
      this.showError('Failed to load product');
      this.close();
    } finally {
      this.setLoading(false);
    }
  }

  renderProduct(product) {
    const container = this.modal.querySelector('.quick-view-product');
    const selectedVariant = product.variants[0];

    container.innerHTML = '<div class="quick-view-product__images">' +
      '<div class="quick-view-product__main-image">' +
        '<img src="' + (selectedVariant.featured_image?.src || product.featured_image) + '" alt="' + product.title + '">' +
      '</div>' +
      (product.images.length > 1 ? this.renderThumbnails(product.images) : '') +
    '</div>' +
    '<div class="quick-view-product__details">' +
      '<h2 class="quick-view-product__title">' + product.title + '</h2>' +
      this.renderPrice(selectedVariant) +
      (product.description ? '<div class="quick-view-product__description">' + product.description + '</div>' : '') +
      this.renderVariants(product) +
      this.renderQuantitySelector() +
      '<button class="quick-view-product__add-to-cart" data-variant-id="' + selectedVariant.id + '">' +
        (selectedVariant.available ? 'Add to Cart' : 'Out of Stock') +
      '</button>' +
      '<a href="' + product.url + '" class="quick-view-product__full-details">View Full Details</a>' +
    '</div>';

    this.currentProduct = product;
    this.selectedVariant = selectedVariant;
  }

  renderThumbnails(images) {
    return '<div class="quick-view-product__thumbnails">' +
      images.map((image, index) =>
        '<div class="quick-view-product__thumbnail' + (index === 0 ? ' quick-view-product__thumbnail--active' : '') + '" data-image-src="' + image + '">' +
          '<img src="' + image + '" alt="Product image ' + (index + 1) + '">' +
        '</div>'
      ).join('') +
    '</div>';
  }

  renderPrice(variant) {
    const compareAtPrice = variant.compare_at_price;
    const price = variant.price;

    if (compareAtPrice && compareAtPrice > price) {
      return '<div class="quick-view-product__price quick-view-product__price--sale">' +
        '<span class="quick-view-product__price-original">' + this.formatMoney(compareAtPrice) + '</span>' +
        '<span class="quick-view-product__price-final">' + this.formatMoney(price) + '</span>' +
      '</div>';
    }

    return '<div class="quick-view-product__price">' + this.formatMoney(price) + '</div>';
  }

  renderVariants(product) {
    if (!product.options || product.options.length === 0) {
      return '';
    }

    return '<div class="quick-view-product__variants">' +
      product.options.map((option, index) => {
        return '<div class="quick-view-product__variant-group">' +
          '<label class="quick-view-product__variant-label">' + option.name + '</label>' +
          '<div class="quick-view-product__variant-options">' +
            option.values.map(value => {
              const isAvailable = this.isOptionAvailable(product, index, value);
              return '<button class="quick-view-product__variant-option' +
                (!isAvailable ? ' quick-view-product__variant-option--unavailable' : '') +
                '" data-option-index="' + index + '" data-option-value="' + value + '">' +
                value +
              '</button>';
            }).join('') +
          '</div>' +
        '</div>';
      }).join('') +
    '</div>';
  }

  renderQuantitySelector() {
    return '<div class="quick-view-product__quantity">' +
      '<label class="quick-view-product__quantity-label">Quantity</label>' +
      '<div class="quick-view-product__quantity-selector">' +
        '<button class="quick-view-product__quantity-button" type="button">-</button>' +
        '<input class="quick-view-product__quantity-input" type="number" value="1" min="1">' +
        '<button class="quick-view-product__quantity-button" type="button">+</button>' +
      '</div>' +
    '</div>';
  }

  selectVariantOption(option) {
    const group = option.closest('.quick-view-product__variant-group');
    group.querySelectorAll('.quick-view-product__variant-option').forEach(opt => {
      opt.classList.remove('quick-view-product__variant-option--active');
    });
    option.classList.add('quick-view-product__variant-option--active');

    this.updateSelectedVariant();
  }

  updateSelectedVariant() {
    const selectedOptions = [];
    this.modal.querySelectorAll('.quick-view-product__variant-option--active').forEach(option => {
      selectedOptions.push(option.dataset.optionValue);
    });

    const variant = this.currentProduct.variants.find(v => {
      return selectedOptions.every((value, index) => v.options[index] === value);
    });

    if (variant) {
      this.selectedVariant = variant;

      // Update price
      const priceContainer = this.modal.querySelector('.quick-view-product__price');
      if (priceContainer) {
        priceContainer.outerHTML = this.renderPrice(variant);
      }

      // Update add to cart button
      const addToCartBtn = this.modal.querySelector('.quick-view-product__add-to-cart');
      if (addToCartBtn) {
        addToCartBtn.dataset.variantId = variant.id;
        addToCartBtn.textContent = variant.available ? 'Add to Cart' : 'Out of Stock';
        addToCartBtn.disabled = !variant.available;
      }

      // Update main image if variant has featured image
      if (variant.featured_image) {
        const mainImage = this.modal.querySelector('.quick-view-product__main-image img');
        if (mainImage) {
          mainImage.src = variant.featured_image.src;
        }
      }
    }
  }

  switchImage(thumbnail) {
    const imageSrc = thumbnail.dataset.imageSrc;
    const mainImage = this.modal.querySelector('.quick-view-product__main-image img');

    if (mainImage && imageSrc) {
      mainImage.src = imageSrc;
    }

    this.modal.querySelectorAll('.quick-view-product__thumbnail').forEach(thumb => {
      thumb.classList.remove('quick-view-product__thumbnail--active');
    });
    thumbnail.classList.add('quick-view-product__thumbnail--active');
  }

  isOptionAvailable(product, optionIndex, optionValue) {
    return product.variants.some(variant => {
      return variant.available && variant.options[optionIndex] === optionValue;
    });
  }

  async addToCart() {
    const variantId = this.selectedVariant?.id;
    const quantityInput = this.modal.querySelector('.quick-view-product__quantity-input');
    const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

    if (!variantId) return;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { variantId, quantity }
      }));

      this.close();
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Failed to add to cart');
    }
  }

  open() {
    this.modal.classList.add('quick-view-modal--active');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.modal.classList.remove('quick-view-modal--active');
    document.body.style.overflow = '';
  }

  setLoading(loading) {
    const container = this.modal.querySelector('.quick-view-product');
    if (loading) {
      container.classList.add('quick-view-product--loading');
    } else {
      container.classList.remove('quick-view-product--loading');
    }
  }

  showError(message) {
    console.error(message);
    // You can implement a toast notification here
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '£' + amount;
  }
}

// Initialize quick view modal
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuickViewModal();
  });
} else {
  new QuickViewModal();
}
